import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReviewFileUserStatus } from '@common/reviews/enums/review-file-user-status.enum';
import { ReviewFileUser } from '@common/reviews/models/review-file-user.model';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareFileReviewerComponent } from '@portal-core/reviews/components/flare-file-reviewer/flare-file-reviewer.component';
import { ReviewFileUsersService } from '@portal-core/reviews/review-file-users/services/review-file-users.service';
import { ReviewFilesService } from '@portal-core/reviews/review-files/services/review-files.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { JoinedLoadingState, joinLoadingStates, LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject, catchError, Observable, of, switchMap, throwError } from 'rxjs';

export interface ReviewEditSnippetDialogData {
  changeCallback: (status: ReviewFileUserStatus) => void;
  featureSetVersion: number;
  navToFileCallback: (src: string) => void;
  onEditorSettingChanged: (name: string, value: boolean) => void;
  reviewer: LicenseUser;
  reviewPackageId: number;
  showConditions: boolean;
  snippetSrc: string;
  stylesEnabled: boolean;
}

@Component({
  selector: 'mc-review-edit-snippet-dialog',
  templateUrl: './review-edit-snippet-dialog.component.html',
  styleUrls: ['./review-edit-snippet-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewEditSnippetDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '130rem', // Matches $page-fill-editor-major-card-max-width in SCSS
    height: '65rem'
  };

  @ViewChild(FlareFileReviewerComponent, { static: false }) flareFileReviewer: FlareFileReviewerComponent;

  collabDocLoadingState: LoadingState<string> = new LoadingState<string>();
  loadingState: JoinedLoadingState<string>;
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  review$: Observable<{ file: ReviewFile, user: ReviewFileUser }>;
  reviewFileLoadingState: LoadingState<string> = new LoadingState<string>();
  stylesheetLoadingState: LoadingState<string> = new LoadingState();

  constructor(
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ReviewEditSnippetDialogData,
    protected dialogRef: MatDialogRef<ReviewEditSnippetDialogComponent>,
    private errorService: ErrorService,
    private reviewFilesService: ReviewFilesService,
    private reviewFileUsersService: ReviewFileUsersService
  ) {
    super(dialog, dialogRef);

    this.loadingState = joinLoadingStates(this.reviewFileLoadingState, this.collabDocLoadingState, this.stylesheetLoadingState, {
      errorStates: [this.reviewFileLoadingState]
    });
  }

  ngOnInit() {
    super.ngOnInit();

    this.reviewFileLoadingState.update(true);

    this.review$ = this.reload$.pipe(
      switchMap(() => {
        this.collabDocLoadingState.update(true);
        this.reviewFileLoadingState.update(true);
        return this.reviewFilesService.getItemByProperties$('ReviewPackageId', this.data.reviewPackageId, 'Path', this.data.snippetSrc).pipe(
          switchMap(reviewFile => {
            if (!reviewFile) {
              return throwError(() => new Error('Unable to find snippet.'));
            }

            return this.reviewFileUsersService.getMyReviewFileUserByUserId$(this.data.reviewer.User.Id, reviewFile.Id).pipe(
              switchMap(reviewFileUser => {
                this.reviewFileLoadingState.update(false);
                return of({ file: reviewFile, user: reviewFileUser });
              }),
              catchError(error => {
                this.reviewFileLoadingState.update(false);
                // Open the editor without submit privileges
                return of({ file: reviewFile, user: null });
              })
            );
          })
        );
      }),
      catchError(error => {
        this.reviewFileLoadingState.update(false, 'Unable to load the snippet file.', this.errorService.getErrorMessages(error));
        return of(null);
      })
    );


    // TODO: Needs to close when navigation changes (like selecting the package overview avatar)
  }

  onCollabDocLoaded(success: boolean) {
    this.collabDocLoadingState.update(false, success ? null : 'Unable to load the snippet.');
  }

  onCollabDocStylesheetLoad() {
    this.stylesheetLoadingState.update(true);
  }

  onCollabDocStylesheetLoaded(error: Error) {
    this.stylesheetLoadingState.update(false, error ? 'There was a problem loading the stylesheet.' : null, this.errorService.getErrorMessages(error));
  }

  onReviewerStatusUpdated(status: ReviewFileUserStatus) {
    this.data.changeCallback(status);
  }

  onRetryLoadFile() {
    this.reload$.next();
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.data.onEditorSettingChanged('showConditions', showConditions);
  }

  onStylesEnabledChanged(stylesEnabled: boolean) {
    this.data.onEditorSettingChanged('stylesEnabled', stylesEnabled);
  }

  protected onNavToFile(src: string) {
    this.closeDialog();
    this.data.navToFileCallback(src);
  }
}

